import React, { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FluentProvider } from '@fluentui/react-components';
import { AuthProvider } from '@/contexts/AuthContext';
import { TeamsProvider } from '@/contexts/TeamsContext';
import { TeamsAuthProvider } from '@/contexts/TeamsAuthContext';
import { useTeams } from '@/hooks/useTeams';
import { SignalRProvider } from '@/contexts/SignalRContext';
import ErrorBoundary from '@/components/ErrorBoundary';
import { routeTree } from './routeTree.gen';
import { isTeamsApp } from '@/utils/appType';
import { getWebTheme, getTeamsFluentTheme } from '@/config/themeConfig';
import './index.css';

// Create a new router instance
const router = createRouter({ routeTree });

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Type guard to check if an error has a message property
 */
function isErrorWithMessage(error: unknown): error is { message: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  );
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on 401 errors
        if (isErrorWithMessage(error) && error.message.includes('401')) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
});

/**
 * Theme Provider Component
 * Handles dynamic theme switching for Teams apps
 */
// eslint-disable-next-line react-refresh/only-export-components
const ThemedApp: React.FC = () => {
  const isTeams = isTeamsApp();
  const [theme, setTheme] = useState(getWebTheme());

  // Always call hook unconditionally (required by React's Rules of Hooks)
  const teamsContext = useTeams();

  useEffect(() => {
    if (isTeams && teamsContext) {
      // Update theme when Teams theme changes
      const newTheme = getTeamsFluentTheme(teamsContext.teamsTheme);
      setTheme(newTheme);
    }
  }, [isTeams, teamsContext]);

  return (
    <FluentProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <SignalRProvider>
            <RouterProvider router={router} />
          </SignalRProvider>
        </AuthProvider>
      </QueryClientProvider>
    </FluentProvider>
  );
};

/**
 * Root App Component
 * Conditionally wraps app with Teams providers if running in Teams
 */
// eslint-disable-next-line react-refresh/only-export-components
const App: React.FC = () => {
  const isTeams = isTeamsApp();

  if (isTeams) {
    // Teams app: wrap with Teams-specific providers
    return (
      <TeamsProvider>
        <TeamsAuthProvider>
          <ThemedApp />
        </TeamsAuthProvider>
      </TeamsProvider>
    );
  }

  // Web app: use standard providers
  return <ThemedApp />;
};

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary
      onError={(error, errorInfo) => {
        // Log errors to console in development
        console.error('Application error:', error, errorInfo);
        // In production, you could send this to an error tracking service
        // e.g., Sentry, Application Insights, etc.
      }}
    >
      <App />
    </ErrorBoundary>
  </StrictMode>
);